import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import type { TerritoryResolution } from '@buzzmap/shared';
import type { TerritoryRouteResolution } from '../hooks/useTerritoryRouteResolution';
import {
  useUserPreferences,
  useTopRankedEvents,
  useWeekendNearbyEvents,
  useIsDesktop,
  useTerritoryRouteResolution,
} from '../hooks';
import { useGalleryScope } from '../hooks/useGalleryScope';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useDiscoveryNav } from '../hooks/useDiscoveryNav';
import { categoryOptions } from '../contexts/UserContext';
import { HERO_MAP_IMAGE, brandBackgroundImage, logoImage } from '../utils/brandImages';
import { categoryHexColors, categoryLongLabel } from '../utils/categories';
import { categoryHref, parseCategorieParam } from '../utils/categoryLinks';
import { requiresTerritoryDocumentNavigation } from '../routes/territoryPaths';
import { trackEvent } from '../utils/analytics';
import { haversineKm } from '../utils/distance';
import { useGeolocationContext } from '../contexts/GeolocationContext';
import { useDiscoveryMode, getDiscoveryMode } from '../contexts/discoveryStore';
import { posthog } from '../lib/analytics/posthogClient';
import { exposeSsrRoot, removeSsrShell, ssrShellHasEventLinks } from '../utils/ssrShell';

import { LocationPill } from '../components/filters/LocationPill';
import { LocalitaPopup } from '../components/filters/LocalitaPopup';
import { SwipeableEventCards } from '../components/homepage/SwipeableEventCards';
import { EventCarousel } from '../components/homepage/EventCarousel';
import { HomeDesktop } from '../components/homepage/HomeDesktop';
import { TerritoryHeader } from '../components/territory/TerritoryHeader';
import { TerritoryShowcase } from '../components/territory/TerritoryShowcase';

import styles from './HomePage.module.css';

// Logo alto al massimo 56 px (64 px da 768 px): largo 159 px (182 px), vedi HomePage.module.css.
const HOME_LOGO_SIZES = '(min-width: 768px) 182px, 159px';

/**
 * T105-FE: la prima carta della pila "Imperdibili" è candidata LCP solo se ne è visibile una parte
 * rilevante senza scorrere. Misurato (context-task-T105.md): la carta (alta ~250 px) inizia a
 * y=594 a 375 px di larghezza e a y=531 a 768 px; con almeno il 40% della carta visibile
 * (594 + 100 = ~700 px) può contendersi l'LCP con il fondo mappa della hero. Sotto resta lazy.
 */
const STACK_VISIBLE_QUERY = '(min-height: 700px)';

export interface HomePageProps {
  territoryRouteResolution?: TerritoryRouteResolution;
}

export function HomePage({ territoryRouteResolution }: HomePageProps = {}) {
  const ownResolution = useTerritoryRouteResolution({
    enabled: territoryRouteResolution === undefined,
  });
  const { resolution, settled } = territoryRouteResolution ?? ownResolution;

  // The shell decision (R15) lives in the two branches below: only they know
  // whether the "Eventi imperdibili" gallery has arrived, and that gallery is
  // what marks the homepage as having real content. `settled` is handed down
  // so both screens wait for the resolution attempt the same way (T015-FE —
  // never remove at mount). HomePage and EventPage stay static imports (C6).
  const isDesktop = useIsDesktop();
  if (isDesktop) {
    return <HomeDesktop resolution={resolution} settled={settled} />;
  }
  return <HomePageMobile resolution={resolution} settled={settled} />;
}

function HomePageMobile({
  resolution,
  settled,
}: {
  resolution: TerritoryResolution | null;
  settled: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLocationChoice, effectiveLocation, locationResolutionPending } = useUserPreferences();
  const { requestLocation } = useGeolocationContext();
  const discoveryMode = useDiscoveryMode();
  // T013: hero/weekend/category targets — the shared builder in v2, today's
  // literals in legacy.
  const nav = useDiscoveryNav();
  // A `?categorie=` selection on the home (2+ categories never normalize to a
  // category path) IS an applied filter on these galleries, so the chips
  // below mark it — the default is "no category", and then nothing is marked.
  const selectedCategories = parseCategorieParam(location.search);

  // Spatial + filter scope for the galleries (T012-FE): v2 queries ALL
  // resolved search-area centers and obeys the explicit URL categories/dates;
  // legacy keeps the single-center/favorites behaviour (plan l.64).
  const galleryScope = useGalleryScope(resolution);
  const stackInView = useMediaQuery(STACK_VISIBLE_QUERY);

  // Galleria SOPRA: popularity ranked, diversified by category.
  // Without centers the hook returns national popularity (no fallbackCity).
  const topRanked = useTopRankedEvents({
    centers: galleryScope.centers,
    coords: galleryScope.coords,
    radiusKm: galleryScope.radiusKm,
    daysAhead: 30,
    limit: 15,
    categories: galleryScope.categories,
    startDate: galleryScope.startDate,
    endDate: galleryScope.endDate,
    skip: locationResolutionPending,
  });

  // Galleria SOTTO: eventi del prossimo weekend nel raggio della preferenza
  // utente, intersecati con le date/categorie esplicithe dell'URL (v2).
  const weekend = useWeekendNearbyEvents({
    centers: galleryScope.centers,
    coords: galleryScope.coords,
    radiusKm: galleryScope.radiusKm,
    limit: 10,
    categories: galleryScope.weekendCategories,
    startDate: galleryScope.startDate,
    endDate: galleryScope.endDate,
    skip: locationResolutionPending,
  });

  // Questo ramo possiede il rilascio del guscio su mobile (HomeDesktop ne
  // è il corrispondente desktop). Attende risoluzione pertinente e settled,
  // galleria senza errori né caricamento e contenuto reale nella galleria
  // o nella vetrina resa; un guscio senza link evento può essere vuoto.
  // La rimozione resta differita come previsto da ssrShell.ts.
  useEffect(() => {
    if (!settled || resolution === null) return;
    if (topRanked.loading) return;
    // La vetrina resa è contenuto reale anche con il feed del periodo vuoto.
    if (
      !topRanked.error &&
      (topRanked.events.length > 0 ||
        (resolution.showcase?.length ?? 0) > 0 ||
        !ssrShellHasEventLinks())
    ) {
      removeSsrShell();
      return;
    }
    // Settled ma non pronto (feed in errore, o vuoto contro un guscio pieno):
    // il guscio resta, ma l'app sotto di esso deve restare usabile (T102-INT).
    exposeSsrRoot();
  }, [settled, resolution, topRanked.loading, topRanked.error, topRanked.events.length]);

  // --- Background refresh (R6): for already-GPS users, silently re-check coords on
  // mount and when location type transitions. If the user moved >5km, update prefs.
  // T011-FE: eliminated in v2 — there is no movement/background GPS refresh once
  // the gate is open (raw GPS can neither become nor update a saved choice).
  // Kept in `legacy` so the pre-activation behaviour stays byte-identical.
  useEffect(() => {
    if (discoveryMode === 'v2') return;
    if (effectiveLocation.type !== 'gps') return;
    const cachedCoords = effectiveLocation.coords;
    const cachedRadius = effectiveLocation.radiusKm;
    requestLocation({ silent: true }).then((result) => {
      if (!result.coords) return;
      // W1 (T011 review): this fix may have resolved AFTER the gate opened
      // (it was acquired during `loading`). A background/movement placement
      // must never run once v2 owns location — re-check the LIVE mode here,
      // not the one captured when the effect started.
      if (getDiscoveryMode() === 'v2') return;
      const dist = haversineKm(result.coords, cachedCoords);
      if (dist > 5) {
        setLocationChoice({
          type: 'gps',
          coords: result.coords,
          radiusKm: cachedRadius,
          ts: Date.now(),
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLocation.type]);

  // --- Popup state (only Località popup remains) ---
  const [openPopup, setOpenPopup] = useState<'localita' | null>(null);
  // Ref to the LocationPill button used as anchor for the desktop popover.
  // On mobile the prop is ignored by FilterPopup (sheet variant is used).
  const localitaPillRef = useRef<HTMLButtonElement>(null);

  return (
    <main className={styles.page}>
      <section className={styles.hero} aria-label="Buzzmap">
        <div className={styles.heroOverlay}>
          <div className={styles.heroIntro}>
            <img {...logoImage(HOME_LOGO_SIZES)} alt="Buzzmap" className={styles.logo} />
            <p className={styles.subtitle}>Scopri cosa succede intorno a te</p>
          </div>
          <div className={styles.actionRow}>
            <LocationPill
              ref={localitaPillRef}
              variant="header"
              onClick={() => setOpenPopup('localita')}
            />
            <button
              type="button"
              className={styles.ctaButton}
              onClick={() => navigate(nav.esploraUrl)}
            >
              <span>Tutti gli eventi</span>
              <span className={styles.ctaArrow} aria-hidden="true">
                →
              </span>
            </button>
          </div>
        </div>
      </section>

      <div
        className={styles.categoryChips}
        style={{ '--hero-map-image': brandBackgroundImage(HERO_MAP_IMAGE) } as CSSProperties}
      >
        <div className={styles.categoryChipsInner}>
          {categoryOptions.map((cat) => {
            // The URL is the filter authority: with `?categorie=` on the home
            // (2+ selections never normalize to a category path) the feed IS
            // filtered, so the applied chips must LOOK selected — never a
            // row where nothing is highlighted while a filter runs.
            const active = selectedCategories.includes(cat.id);
            return (
              <Link
                key={cat.id}
                // T013: v2 builds the chip through the shared builder; legacy
                // keeps `categoryHref`. `reloadDocument` is upstream's period
                // hand-off: leaving/entering a period-in-path page must refetch
                // the SSR head, and it evaluates the SAME target the link uses.
                to={nav.categoryUrl(cat.id) ?? categoryHref(cat.id, resolution, location)}
                reloadDocument={requiresTerritoryDocumentNavigation(
                  location.pathname,
                  location.search,
                  nav.categoryUrl(cat.id) ?? categoryHref(cat.id, resolution, location)
                )}
                className={styles.categoryChip}
                aria-current={active ? 'true' : undefined}
                title={categoryLongLabel(cat.id)}
                onClick={() => {
                  posthog.capture('category_selected', {
                    category: cat.id,
                    label: cat.label,
                    source: 'home',
                  });
                }}
              >
                <span
                  className={styles.categoryDot}
                  style={{ backgroundColor: categoryHexColors[cat.id] }}
                  aria-hidden="true"
                />
                {cat.label}
              </Link>
            );
          })}
        </div>
      </div>

      <div className={styles.mapBand}>
        <TerritoryHeader resolution={resolution} hideCategoryList variant="plain" />
        <SwipeableEventCards
          events={topRanked.events}
          loading={topRanked.loading}
          prioritizeFirstCard={stackInView}
        />
      </div>

      <div className={styles.showcaseBand}>
        <TerritoryShowcase items={resolution?.showcase ?? []} />
      </div>

      <div className={styles.weekendBand}>
        <EventCarousel
          events={weekend.events}
          loading={weekend.loading}
          heading="Questo weekend"
          onEventClick={(id) =>
            navigate(`/event/${id}`, {
              state: { backgroundLocation: location },
            })
          }
          onSeeAll={() => {
            posthog.capture('weekend_explore_clicked', {
              weekend_start: weekend.weekendStart,
              weekend_end: weekend.weekendEnd,
            });
            // T013: the CTA preserves its explicit dates; in v2 it lands on
            // the territorial Esplora surface of the current URL.
            navigate(nav.weekendUrl(weekend.weekendStart, weekend.weekendEnd));
          }}
        />
      </div>

      <LocalitaPopup
        open={openPopup === 'localita'}
        onClose={() => setOpenPopup(null)}
        anchorEl={localitaPillRef.current}
        onChange={() => {
          trackEvent('filter_apply', { type: 'localita' });
        }}
      />
    </main>
  );
}
